import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

const THEME_KEY = '@portal-due-it/theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

const ThemeContext = createContext({});

function storedTheme() {
  try {
    const theme = localStorage.getItem(THEME_KEY);
    return theme === 'dark' || theme === 'light' ? theme : null;
  } catch {
    return null;
  }
}

function systemTheme() {
  try {
    return window.matchMedia && window.matchMedia(DARK_QUERY).matches
      ? 'dark'
      : 'light';
  } catch {
    return 'light';
  }
}

const ThemeProvider = ({ children }) => {
  // Sem escolha salva o tema acompanha o sistema — e continua acompanhando
  // enquanto o usuário não clicar no botão (`isExplicit`).
  const [theme, setThemeState] = useState(() => storedTheme() || systemTheme());
  const [isExplicit, setIsExplicit] = useState(() => !!storedTheme());

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  useEffect(() => {
    if (isExplicit || !window.matchMedia) return undefined;

    const query = window.matchMedia(DARK_QUERY);
    const listener = event => setThemeState(event.matches ? 'dark' : 'light');

    query.addEventListener('change', listener);
    return () => query.removeEventListener('change', listener);
  }, [isExplicit]);

  const setTheme = useCallback(next => {
    try {
      localStorage.setItem(THEME_KEY, next);
    } catch {
      // Navegação privada: o tema vale só para esta sessão.
    }
    setIsExplicit(true);
    setThemeState(next);
  }, []);

  const toggleTheme = useCallback(
    () => setTheme(theme === 'dark' ? 'light' : 'dark'),
    [setTheme, theme]
  );

  const value = useMemo(
    () => ({ theme, isDark: theme === 'dark', setTheme, toggleTheme }),
    [theme, setTheme, toggleTheme]
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

function useTheme() {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }

  return context;
}

export { ThemeProvider, useTheme };
